import {useFormik} from 'formik';
import {useNavigate} from 'react-router';
import * as Yup from 'yup';
import {AxiosError} from 'axios';
import {useFundraisers} from '../../../entities';
import {CreateFundraiserDTO} from '../../../entities';
import {useSchemes} from '../../../shared';

export const initialFormState: CreateFundraiserDTO = {
  general: {
    name: '',
    shortName: '',
    shortDescription: '',
    detailedDescription: '',
    fundId: '',
    regionId: '',
    categoryIds: [],
    externalId: null,
  },
  financialAndTiming: {
    requiredAmount: null,
    commissionPercentage: 0,
    end: null,
  },
  isUrgent: false,
  feedImageUploadId: '',
};

type UseAddAppealFormProps = {
  navigateAfterSuccess?: boolean;
};

export const useAddAppealForm = ({navigateAfterSuccess = true}: UseAddAppealFormProps = {}) => {
  const navigate = useNavigate();
  const {nameSchema, shortNameSchema, shortDescriptionSchema, detailedDescriptionSchema} = useSchemes();
  const {createFundraiserMutation} = useFundraisers();

  const onSubmit = async (values: CreateFundraiserDTO) => {
    try {
      await createFundraiserMutation.mutateAsync({
        ...values,
        general: {
          ...values.general,
          shortName: values.general.shortName || null,
        },
      });
      if (navigateAfterSuccess) {
        navigate('/fundraisings/appeals');
      }
    } catch (error) {
      console.error('Ошибка при создании сбора:', error);
    }
  };

  const formik = useFormik({
    initialValues: initialFormState,
    validateOnChange: true,
    validationSchema: Yup.object({
      general: Yup.object({
        name: nameSchema,
        shortName: shortNameSchema,
        shortDescription: shortDescriptionSchema,
        detailedDescription: detailedDescriptionSchema,
        fundId: Yup.string().required('Обязательное поле'),
        regionId: Yup.string().required('Обязательное поле'),
        categoryIds: Yup.array()
          .of(Yup.string())
          .min(1, 'Выберите хотя бы одну категорию')
          .required('Обязательное поле'),
      }),
      financialAndTiming: Yup.object({
        requiredAmount: Yup.number()
          .typeError('Должно быть числовым значением')
          .min(1, 'Сумма должна быть больше 0')
          .nullable(),
        commissionPercentage: Yup.number()
          .typeError('Должно быть числовым значением')
          .min(0, 'Процент должен быть положительным числом')
          .required('Обязательное поле'),
        end: Yup.date().nullable(),
      }),
      isUrgent: Yup.boolean(),
    }),
    onSubmit,
  });

  return {
    ...formik,
    isLoading: createFundraiserMutation.isPending,
    submitError: createFundraiserMutation.error
      ? (createFundraiserMutation.error as AxiosError<ApiError>).response?.data
      : undefined,
    submitForm: () => formik.handleSubmit(),
  };
};
